import { useState, type FormEvent } from 'react';
import { Alert, Box, Button, Stack } from '@mui/material';
import { PASSWORD_MIN_LENGTH } from '@pd/shared';
import { api, ApiError } from '../../api/client';
import { t } from '../../i18n/sq';
import { PasswordField } from '../../components/PasswordField';

/** Current password plus the new one twice. Used on first sign-in and in Settings. */
export function ChangePasswordForm({
  forced = false,
  onChanged,
}: {
  /** First sign-in: explains why the change is required. */
  forced?: boolean;
  /** Called after a successful change; without it the form clears and confirms in place. */
  onChanged?: () => void;
}) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [busy, setBusy] = useState(false);
  const mismatch = confirm.length > 0 && confirm !== next;

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setSuccess(false);
    try {
      await api.post('/auth/change-password', { currentPassword: current, newPassword: next });
      if (onChanged) {
        onChanged();
      } else {
        setSuccess(true);
        setCurrent('');
        setNext('');
        setConfirm('');
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t.common.unexpectedError);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Box component="form" onSubmit={submit}>
      <Stack spacing={2.5}>
        {forced && <Alert severity="warning">{t.auth.mustChangePassword}</Alert>}
        {error && <Alert severity="error">{error}</Alert>}
        {success && <Alert severity="success">{t.auth.passwordChanged}</Alert>}
        <PasswordField
          label={t.auth.currentPassword}
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
          autoComplete="current-password"
          required
        />
        <PasswordField
          label={t.auth.newPassword}
          value={next}
          onChange={(e) => setNext(e.target.value)}
          autoComplete="new-password"
          helperText={t.auth.passwordHint}
          required
        />
        <PasswordField
          label={t.auth.confirmPassword}
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          autoComplete="new-password"
          error={mismatch}
          helperText={mismatch ? t.auth.passwordsDontMatch : undefined}
          required
        />
        <Button
          type="submit"
          variant="contained"
          size="large"
          loading={busy}
          disabled={next.length < PASSWORD_MIN_LENGTH || mismatch}
        >
          {t.common.save}
        </Button>
      </Stack>
    </Box>
  );
}
